import React from "react";
import {
  BrainCircuit,
  Cloud,
  Code,
  Database,
  Briefcase,
  Award,
  GraduationCap,
  Mic,
  MountainSnow,
  Bike,
  Tent,
  Ship,
  Plane,
} from "lucide-react";

const badgePill =
  "inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs";

const skillTone = {
  purple: "bg-purple-500/20 text-purple-100",
  blue: "bg-sky-500/20 text-sky-100",
  green: "bg-emerald-500/20 text-emerald-100",
  orange: "bg-orange-500/20 text-orange-100",
};

const bulletTone = {
  blue: "bg-sky-500/20 text-sky-200 ring-1 ring-sky-400/30",
  purple: "bg-purple-500/20 text-purple-200 ring-1 ring-purple-400/30",
  green: "bg-emerald-500/20 text-emerald-200 ring-1 ring-emerald-400/30",
  orange: "bg-orange-500/20 text-orange-200 ring-1 ring-orange-400/30",
  pink: "bg-pink-500/20 text-pink-200 ring-1 ring-pink-400/30",
  red: "bg-rose-500/20 text-rose-200 ring-1 ring-rose-400/30",
};

const About = () => {
  const highlightTags = [
    { icon: <BrainCircuit size={14} />, label: "AI/ML Engineer" },
    { icon: <Cloud size={14} />, label: "Cloud & MLOps" },
    { icon: <Code size={14} />, label: "Full-Stack Engineer" },
    { icon: <Database size={14} />, label: "Data Scientist" },
  ];

  const skillCards = [
    {
      icon: <BrainCircuit size={18} />,
      title: "AI & Machine Learning",
      tone: "purple",
      tags: [
        "Generative AI",
        "Large Language Models (LLMs)",
        "Retrieval Augmented Generation (RAG)",
        "Deep Learning",
        "Transformers",
        "NLP",
        "Computer Vision",
        "Model Fine Tuning",
        "Agentic AI",
        "Prompt Engineering",
        "XGBoost",
        "Model Evaluation & Optimization",
        "Feature Engineering",
        "MCP",
      ],
    },
    {
      icon: <Cloud size={18} />,
      title: "Cloud Architecture & MLOps",
      tone: "blue",
      tags: [
        "AWS (SageMaker, Athena, CloudWatch, IAM, Redis)",
        "GCP",
        "Azure",
        "Docker",
        "Kubernetes",
        "MLflow",
        "Airflow",
        "CI/CD Pipelines",
        "Snowflake",
        "Databricks",
        "Hugging Face",
        "Jenkins",
        "GitHub Actions",
        "Splunk",
        "DBT",
      ],
    },
    {
      icon: <Code size={18} />,
      title: "Programming & Backend",
      tone: "green",
      tags: [
        "Python",
        "Java",
        "Go",
        "C++",
        "JavaScript",
        "TypeScript",
        "R",
        "SAS",
        "Spring Boot",
        "Flask",
        "React JS",
        "Redux",
        "Node JS",
        "Express",
        "Hibernate",
        "JUnit",
        "Selenium",
        "SQL",
        "NoSQL",
      ],
    },
    {
      icon: <Database size={18} />,
      title: "Data Engineering & Analytics",
      tone: "orange",
      tags: [
        "PostgreSQL",
        "Oracle",
        "MySQL",
        "MongoDB",
        "Cassandra",
        "DynamoDB",
        "SQL Server",
        "Kafka",
        "Spark",
        "Hadoop",
        "ETL Pipelines",
        "Statistical Modeling",
        "Forecasting",
        "Time Series Analysis",
        "Data Visualization",
        "Power BI",
        "Tableau",
      ],
    },
  ];

  const workExperience = [
    {
      role: "Data Scientist",
      company: "Grand Valley State University (Kirkhof College of Nursing)",
      location: "Grand Rapids, MI",
      dates: "Sep 2024 - Present",
      bullets: [
        {
          tag: "RAG",
          tone: "blue",
          body: "Architected a RAG pipeline with HNSW-indexed vector search on Qdrant (chosen over PGVector for sub-50ms p95 approximate nearest neighbor retrieval), embedding the institution's full document corpus (5,000+ records) using sentence-transformers with recursive chunking and metadata filtering.",
        },
        {
          tag: "AGENTIC",
          tone: "purple",
          body: "Built LangChain-orchestrated agentic retrieval with cross-encoder reranking, structured JSON output enforcement at 95%+ parsing reliability, and RAGAS-based evaluation (faithfulness, answer relevancy, context precision) across 4+ LLM configurations, reducing hallucination rates by 30%.",
        },
        {
          tag: "COST OPT",
          tone: "green",
          body: "Eliminated $25K+ in annual enterprise LLM API costs by hosting open-weight Ollama models on the university's on-premise HPC cluster with 4-bit quantization and batched inference, preserving institutional data privacy at sub-second response latency.",
        },
        {
          tag: "ANALYTICS",
          tone: "orange",
          body: "Architected the institution's first real-time analytics platform using Power BI with DirectQuery to Azure Synapse Serverless, with star-schema dimension tables, indexed views on time-series data, and 20+ DAX measures delivering live operational visibility for college administration where none existed.",
        },
        {
          tag: "ML",
          tone: "pink",
          body: "Built an ML-based Student Early Warning System on XGBoost (92% accuracy) analyzing GPA and attendance patterns across the entire program cohort (500+ students), enabling advisors to intervene 4–6 weeks earlier.",
        },
        {
          tag: "LEADERSHIP",
          tone: "red",
          body: "Directed all data operations for the college, supervising a team of 4 across reporting, analytics, and database management, reducing request turnaround by 40% and improving data quality by 25%.",
        },
      ],
      tags: [
        "Python",
        "RAG",
        "Qdrant (HNSW)",
        "LangChain",
        "Ollama (on-prem HPC)",
        "RAGAS",
        "XGBoost",
        "Power BI (DAX)",
        "Azure Synapse",
        "Team Leadership",
      ],
    },
    {
      role: "Data Scientist (Biostatistician) · Part-Time",
      company: "Corewell Health",
      location: "Grand Rapids, MI",
      dates: "May 2024 - May 2025",
      bullets: [
        {
          tag: "NLP / LLM",
          tone: "purple",
          body: "Integrated NLP and LLM-driven classification models with prompt engineering into Power BI dashboards, enabling automated anomaly detection and surfacing critical patterns across clinical datasets.",
        },
        {
          tag: "BIOSTATS",
          tone: "blue",
          body: "Conducted hypothesis-driven statistical analyses (t-tests, ANOVA, chi-square, log-rank, Cox proportional hazards) on clinical data, supporting publication-grade research outputs and 4 IRB-approved initiatives.",
        },
        {
          tag: "RESEARCH",
          tone: "orange",
          body: "Authored statistical analysis plans for 6+ clinical studies spanning retrospective and prospective designs, reducing protocol revision cycles by 30%.",
        },
        {
          tag: "DATA OPS",
          tone: "green",
          body: "Developed Python data pipelines that unified 12+ disparate clinical datasets into ML-ready formats, accelerating model training cycles by 45% and eliminating 20+ hours of weekly manual data wrangling.",
        },
        {
          tag: "OPEN SOURCE",
          tone: "red",
          body: "Fixed a critical parsing flaw in the RISmed R package, contributing a merged PR to the upstream open-source repository, now improving data acquisition accuracy for 200+ internal researchers and the broader R community.",
        },
      ],
      tags: [
        "R",
        "Python",
        "NLP",
        "LLMs",
        "Cox Regression",
        "Power BI",
        "FDA / IRB",
        "Open Source PR",
        "SQL",
      ],
    },
    {
      role: "Data Analyst",
      company: "Grand Valley State University (Charter Schools Office)",
      location: "Grand Rapids, MI",
      dates: "May 2023 - Apr 2024",
      bullets: [
        {
          tag: "MLOPS",
          tone: "purple",
          body: "Engineered end-to-end MLOps pipelines using scikit-learn regression models, MLflow for experiment tracking, Docker for containerized deployment, and cron-based automated retraining, achieving a 22% RMSE improvement over baseline.",
        },
        {
          tag: "ETL",
          tone: "orange",
          body: "Designed centralized ETL pipelines powering strategic dashboards in Tableau for 10+ school districts, enabling administrators to track enrollment, funding, and performance metrics in near real time, accelerating key decisions by 60%.",
        },
      ],
      tags: ["Scikit-learn", "MLflow", "Docker", "ETL", "Tableau", "MLOps"],
    },
    {
      role: "Software Development Engineer (Analyst)",
      company: "Deloitte",
      location: "Hyderabad, India",
      dates: "Feb 2021 - Aug 2022",
      bullets: [
        {
          tag: "BACKEND",
          tone: "blue",
          body: "Elevated system reliability to 99.5% uptime by overhauling backend API architecture with Java Spring and Hibernate, implementing connection pooling, lazy loading, and caching strategies that reduced API latency by 25% for 10,000+ daily active users.",
        },
        {
          tag: "SECURITY",
          tone: "green",
          body: "Secured and streamlined deployment workflows by implementing SSO authentication with AWS tokens and configuring environment-specific domains, cutting access-related support tickets by 30%.",
        },
        {
          tag: "MICROSERVICES",
          tone: "purple",
          body: "Implemented reusable microservices using Spring Boot and Hibernate with structured exception handling and logging frameworks, improving diagnosability and reducing production defects by 20%.",
        },
      ],
      tags: [
        "Java",
        "Spring Boot",
        "Hibernate",
        "AWS",
        "SSO",
        "Microservices",
        "Connection Pooling",
        "CI/CD",
      ],
    },
    {
      role: "Senior Systems Engineer",
      company: "Infosys",
      location: "Hyderabad, India",
      dates: "Feb 2018 - Jan 2021",
      bullets: [
        {
          tag: "FRONTEND",
          tone: "purple",
          body: "Built a library of 30+ reusable, responsive ReactJS components with Redux and modern CSS frameworks, developing interactive web tables that handled 10x larger datasets while maintaining sub-second render times.",
        },
        {
          tag: "TESTING",
          tone: "orange",
          body: "Established a comprehensive testing and error-handling framework across frontend (Enzyme) and backend (JUnit, Mockito, Powermockito), reducing production defects by 30% and saving 200+ engineering hours per quarter.",
        },
        {
          tag: "BACKEND",
          tone: "blue",
          body: "Profiled and re-engineered backend service layers using Java Spring MVC, implementing caching, pagination, and query optimization across RESTful APIs and built CI/CD pipelines with Jenkins and Git, achieving 99.5% deployment success rate across 20+ production releases.",
        },
      ],
      tags: [
        "React",
        "Redux",
        "Java Spring MVC",
        "JUnit",
        "Jenkins",
        "REST APIs",
      ],
    },
  ];

  const certifications = [
    {
      title: "Claude Code in Action",
      issuer: "Anthropic",
      issued: "Apr 2026",
      credentialId: "hybf546235cn",
    },
    {
      title: "Introduction to Model Context Protocol",
      issuer: "Anthropic",
      issued: "Apr 2026",
      credentialId: "2betvsiggxyb",
    },
    {
      title: "AI Fluency: Framework & Foundations",
      issuer: "Anthropic",
      issued: "Apr 2026",
      credentialId: "pb77xjhif3ru",
    },
    {
      title: "AI 501: Introduction to AI",
      issuer: "Grand Valley State University",
      issued: "Completed",
    },
    {
      title: "AI 502: Generative AI",
      issuer: "Grand Valley State University",
      issued: "Completed",
    },
    {
      title: "SAS Certified Associate",
      issuer: "SAS Institute",
      issued: "Completed",
    },
    {
      title: "Infosys Certified Python Developer",
      issuer: "Infosys",
      issued: "Completed",
    },
    {
      title: "Infosys Certified React JS Developer",
      issuer: "Infosys",
      issued: "Completed",
    },
    {
      title: "Infosys Certified Agile Developer",
      issuer: "Infosys",
      issued: "Completed",
    },
  ];

  const education = [
    {
      degree: "Master of Science in Data Science & Analytics",
      school: "Grand Valley State University",
      dates: "Aug 2022 - April 2024",
      gpa: "3.9 / 4.0",
      honors: ["Dean's Scholarship", "Applied Analytics Track"],
      activities: [
        "Data Science Club Mentor",
        "Graduate Teaching Assistant",
        "AI Research Lab",
      ],
    },
    {
      degree: "Bachelor of Technology in Mechanical Engineering",
      school: "JNTU Kakinada",
      dates: "Aug 2013 - May 2017",
      gpa: "8.1 / 10",
      honors: [],
      activities: [],
    },
  ];

  const talks = [
    {
      title: "AI Awareness Lunch & Learn",
      venue: "Kirkhof College of Nursing, GVSU",
      date: "Aug 2025",
      summary:
        "Hands-on session on LLMs, Agentic AI, RAG systems, and MCP protocols for 60+ non-technical healthcare professionals. 95% positive feedback rating.",
    },
  ];

  const hobbies = [
    {
      icon: <MountainSnow size={16} />,
      title: "Freestyle Skiing",
      text: "Pushing boundaries and perfecting technique on the slopes.",
    },
    {
      icon: <Bike size={16} />,
      title: "Road Biking",
      text: "Staying active and enjoying long rides in nature.",
    },
    {
      icon: <Tent size={16} />,
      title: "Camping",
      text: "Disconnecting to explore new landscapes and trails.",
    },
    {
      icon: <Ship size={16} />,
      title: "Boating",
      text: "Making waves and enjoying water adventures.",
    },
    {
      icon: <Plane size={16} />,
      title: "Traveling",
      text: "Exploring new cultures and broadening perspectives.",
    },
  ];

  return (
    <div
      className="px-4 pt-16 pb-20 sm:px-6 lg:px-20"
      style={{ color: "var(--text)" }}
    >
      <section className="mx-auto max-w-6xl">
        <div className="text-center">
          <div
            className="mx-auto mb-8 h-28 w-28 overflow-hidden rounded-full border shadow-[0_25px_45px_rgba(0,0,0,0.45)]"
            style={{ borderColor: "var(--border)" }}
          >
            <img
              src="/Chamana-Satya.jpg"
              alt="Satya Chamana"
              className="h-full w-full object-cover"
            />
          </div>
          <h1 className="text-3xl font-semibold md:text-4xl">Satya Chamana</h1>
          <p className="mt-1 text-sm" style={{ color: "var(--text-muted)" }}>
            AI Engineer · ML · Full-Stack
          </p>
          <div className="mt-4 flex flex-wrap justify-center gap-2">
            {highlightTags.map((item) => (
              <span
                key={item.label}
                className={badgePill}
                style={{ backgroundColor: "var(--pill)" }}
              >
                {item.icon}
                {item.label}
              </span>
            ))}
          </div>
        </div>

        <div className="mt-16 space-y-8">
          <article
            className="rounded-[36px] border px-8 py-10 text-[0.95rem] leading-relaxed shadow-[0_30px_55px_rgba(0,0,0,0.45)]"
            style={{
              backgroundColor: "var(--bg-soft)",
              borderColor: "var(--border)",
              color: "var(--text-muted)",
            }}
          >
            <div
              className="flex items-center gap-3"
              style={{ color: "var(--text)" }}
            >
              <Briefcase size={20} />
              <h2 className="text-xl font-semibold">Professional Summary</h2>
            </div>
            <div className="mt-5 space-y-5 text-left">
              <p>
                AI/ML Engineer with 8+ years shipping intelligent systems that
                serve 10,000+ users at 99.5% uptime. Built RAG pipelines
                 with sub-50ms retrieval, XGBoost models at 92% accuracy,
                and computer vision systems running at 25+ FPS in production.
                Deep hands-on experience with LLMs, LangChain, vector search,
                and fine-tuning (LoRA) on AWS and Azure. M.S. in Data Science
                (3.9 GPA).
              </p>
              <p>
                Eight years compounding across SDE → Data Science → AI
                Engineering. Built Java Spring Boot microservices and React UIs
                at Infosys (Apple, Verizon) and Deloitte. Layered statistical
                modeling (Cox proportional hazards, ANOVA, hypothesis testing)
                and MLOps (MLflow, Docker, Airflow) through the Master's at
                GVSU. Now ship end-to-end, from database schema to RAG
                architecture to real-time inference APIs.
              </p>
              <p>
                Lead a team of 4 across reporting, analytics, and database
                operations. Replaced $25K+/year of enterprise LLM API spend by
                hosting Ollama on the on-prem HPC cluster. Comfortable owning
                both the architecture and the bullet that explains it to a
                non-technical stakeholder.
              </p>
            </div>
          </article>
        </div>
      </section>

      <section className="mt-14">
        <div
          className="flex items-center gap-3"
          style={{ color: "var(--text)" }}
        >
          <Code size={20} />
          <h2 className="text-2xl font-semibold">Skills & Expertise</h2>
        </div>
        <div className="mt-6 grid gap-6 md:grid-cols-2">
          {skillCards.map((card) => (
            <article
              key={card.title}
              className="rounded-3xl border p-6"
              style={{
                backgroundColor: "var(--card)",
                borderColor: "var(--border)",
              }}
            >
              <div className="flex items-center gap-3">
                <span
                  className={`inline-flex h-9 w-9 items-center justify-center rounded-xl ${skillTone[card.tone]}`}
                >
                  {card.icon}
                </span>
                <h3 className="text-lg font-semibold">{card.title}</h3>
              </div>
              <div className="mt-4 flex flex-wrap gap-2">
                {card.tags.map((tag) => (
                  <span
                    key={tag}
                    className={`rounded-full px-3 py-1 text-[0.7rem] font-semibold ${skillTone[card.tone]}`}
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </article>
          ))}
        </div>
      </section>

      <section className="mt-16">
        <div
          className="flex items-center gap-3"
          style={{ color: "var(--text)" }}
        >
          <Briefcase size={20} />
          <h2 className="text-2xl font-semibold">Work Experience</h2>
        </div>
        <div className="mt-6 space-y-6">
          {workExperience.map((job) => (
            <article
              key={job.role}
              className="rounded-[32px] border px-6 py-8 shadow-[0_25px_45px_rgba(0,0,0,0.35)] transition-shadow hover:shadow-[0_30px_55px_rgba(0,0,0,0.45)]"
              style={{
                backgroundColor: "var(--card)",
                borderColor: "var(--border)",
              }}
            >
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div className="space-y-1">
                  <h3 className="text-xl font-semibold">{job.role}</h3>
                  <p className="text-sm font-semibold">{job.company}</p>
                  <p className="text-sm" style={{ color: "var(--text-muted)" }}>
                    {job.location}
                  </p>
                </div>
                <span
                  className="text-sm"
                  style={{ color: "var(--text-muted)" }}
                >
                  {job.dates}
                </span>
              </div>
              <div className="mt-5 space-y-3">
                {job.bullets.map((item) => (
                  <p
                    key={item.body}
                    className="text-[0.95rem] leading-relaxed"
                    style={{ color: "var(--text-muted)" }}
                  >
                    <span
                      className={`mr-2 inline-block whitespace-nowrap rounded-md px-2 py-0.5 text-[0.6rem] font-bold uppercase tracking-wider align-middle ${bulletTone[item.tone]}`}
                    >
                      {item.tag}
                    </span>
                    {item.body}
                  </p>
                ))}
              </div>
              <div className="mt-4 flex flex-wrap gap-2">
                {job.tags.map((tag) => (
                  <span
                    key={tag}
                    className="rounded-full border px-3 py-1 text-[0.78rem]"
                    style={{
                      borderColor: "var(--border)",
                      color: "var(--text-muted)",
                    }}
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </article>
          ))}
        </div>
      </section>

      <section className="mt-14">
        <div
          className="flex items-center gap-3"
          style={{ color: "var(--text)" }}
        >
          <Award size={20} />
          <h2 className="text-2xl font-semibold">Certifications</h2>
        </div>
        <div className="mt-6 grid gap-6 md:grid-cols-3">
          {certifications.map((cert) => (
            <article
              key={cert.title}
              className="rounded-3xl border p-6"
              style={{
                backgroundColor: "var(--card)",
                borderColor: "var(--border)",
              }}
            >
              <h3 className="text-base font-semibold">{cert.title}</h3>
              <p
                className="mt-2 text-sm"
                style={{ color: "var(--text-muted)" }}
              >
                {cert.issuer}
              </p>
              <span
                className="mt-2 block text-xs"
                style={{ color: "var(--text-muted)" }}
              >
                {cert.issued}
              </span>
              {cert.credentialId && (
                <span
                  className="mt-1 block text-[0.65rem] font-mono"
                  style={{ color: "var(--text-muted)", opacity: 0.7 }}
                >
                  ID: {cert.credentialId}
                </span>
              )}
            </article>
          ))}
        </div>
      </section>

      <section className="mt-14">
        <div
          className="flex items-center gap-3"
          style={{ color: "var(--text)" }}
        >
          <GraduationCap size={20} />
          <h2 className="text-2xl font-semibold">Education</h2>
        </div>
        <div className="mt-6 space-y-6">
          {education.map((edu) => (
            <article
              key={edu.degree}
              className="rounded-3xl border p-6"
              style={{
                backgroundColor: "var(--card)",
                borderColor: "var(--border)",
              }}
            >
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div>
                  <h3 className="text-lg font-semibold">{edu.degree}</h3>
                  <p className="text-sm" style={{ color: "var(--text-muted)" }}>
                    {edu.school}
                  </p>
                </div>
                <div
                  className="text-right text-xs"
                  style={{ color: "var(--text-muted)" }}
                >
                  <span className="block">{edu.dates}</span>
                  <strong className="text-sm" style={{ color: "var(--text)" }}>
                    GPA: {edu.gpa}
                  </strong>
                </div>
              </div>
              {edu.honors.length > 0 && (
                <div className="mt-4 flex flex-wrap gap-2">
                  {edu.honors.map((item) => (
                    <span
                      key={item}
                      className="rounded-full px-3 py-1 text-[0.7rem]"
                      style={{
                        backgroundColor: "var(--pill)",
                        color: "var(--text-muted)",
                      }}
                    >
                      {item}
                    </span>
                  ))}
                </div>
              )}
              {edu.activities.length > 0 && (
                <div
                  className="mt-4 text-sm"
                  style={{ color: "var(--text-muted)" }}
                >
                  <p className="font-semibold" style={{ color: "var(--text)" }}>
                    Activities and Societies:
                  </p>
                  <ul className="mt-2 list-disc space-y-1 pl-5">
                    {edu.activities.map((item) => (
                      <li key={item}>{item}</li>
                    ))}
                  </ul>
                </div>
              )}
            </article>
          ))}
        </div>
      </section>

      <section className="mt-14">
        <div
          className="flex items-center gap-3"
          style={{ color: "var(--text)" }}
        >
          <Mic size={20} />
          <h2 className="text-2xl font-semibold">Talks & Presentations</h2>
        </div>
        <div className="mt-6 space-y-6">
          {talks.map((talk) => (
            <article
              key={talk.title}
              className="rounded-3xl border p-6"
              style={{
                backgroundColor: "var(--card)",
                borderColor: "var(--border)",
              }}
            >
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div>
                  <h3 className="text-lg font-semibold">{talk.title}</h3>
                  <p className="text-sm" style={{ color: "var(--text-muted)" }}>
                    {talk.venue}
                  </p>
                </div>
                <span
                  className="text-sm"
                  style={{ color: "var(--text-muted)" }}
                >
                  {talk.date}
                </span>
              </div>
              <p
                className="mt-3 text-sm leading-relaxed"
                style={{ color: "var(--text-muted)" }}
              >
                {talk.summary}
              </p>
            </article>
          ))}
        </div>
      </section>

      <section className="mt-14">
        <div
          className="flex items-center gap-3"
          style={{ color: "var(--text)" }}
        >
          <Award size={20} />
          <h2 className="text-2xl font-semibold">Hobbies & Interests</h2>
        </div>
        <div className="mt-6 grid gap-6 md:grid-cols-3">
          {hobbies.map((hobby) => (
            <article
              key={hobby.title}
              className="rounded-3xl border p-6"
              style={{
                backgroundColor: "var(--card)",
                borderColor: "var(--border)",
              }}
            >
              <span
                className={badgePill}
                style={{ backgroundColor: "var(--pill)" }}
              >
                {hobby.icon}
                {hobby.title}
              </span>
              <p
                className="mt-3 text-sm"
                style={{ color: "var(--text-muted)" }}
              >
                {hobby.text}
              </p>
            </article>
          ))}
        </div>
      </section>
    </div>
  );
};

export default About;
